import React, { useEffect, useMemo, useRef, useState } from "react";
import { renderLatex } from "./MathText";
import mathSymbols from "./symbols/mathSymbols";
import physicsSymbols from "./symbols/physicsSymbols";
import chemistrySymbols from "./symbols/chemistrySymbols";
import "../styles/SymbolToolbar.css";

const SUBJECTS = [
  { key: "math", label: "Maths", icon: "bi-calculator", categories: mathSymbols },
  { key: "physics", label: "Physics", icon: "bi-lightning-charge", categories: physicsSymbols },
  { key: "chemistry", label: "Chemistry", icon: "bi-droplet-half", categories: chemistrySymbols },
];

const RECENT_KEY = "jupiter-admin:recent-symbols";
const RECENT_LIMIT = 16;
const SEARCH_LIMIT = 150;

// Flat, searchable list of every snippet across all subjects
const ALL_SYMBOLS = SUBJECTS.flatMap((subject) =>
  subject.categories.flatMap((category) =>
    category.symbols.map(([latex, description]) => ({
      latex,
      description,
      subject: subject.label,
      haystack: `${description} ${category.title} ${latex}`.toLowerCase(),
    }))
  )
);

// Picks the tab that matches a section name, e.g. "Physics" or "रसायन विज्ञान"
export const subjectForSection = (name = "") => {
  const text = name.toLowerCase();
  if (/chem|रसायन/.test(text)) return "chemistry";
  if (/phys|भौतिक/.test(text)) return "physics";
  if (/math|गणित/.test(text)) return "math";
  return null;
};

const searchSymbols = (query) => {
  const phrase = query.toLowerCase();
  const terms = phrase.split(/\s+/).filter(Boolean);
  const seen = new Set();
  return ALL_SYMBOLS.filter((item) => terms.every((term) => item.haystack.includes(term)))
    .map((item) => {
      const description = item.description.toLowerCase();
      // "Sodium (Z = 11)" is an exact hit for "sodium"; "Sodium chloride" only a prefix hit
      const name = description.replace(/\s*\(.*\)$/, "");
      const score =
        name === phrase ? 0 : description.startsWith(phrase) ? 1 : description.includes(terms[0]) ? 2 : 3;
      return { ...item, score };
    })
    .sort((a, b) => a.score - b.score)
    .filter((item) => {
      const key = `${item.latex}|${item.description}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, SEARCH_LIMIT);
};

const readRecent = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RECENT_KEY));
    return Array.isArray(stored) ? stored.filter((item) => item && item.latex) : [];
  } catch {
    return [];
  }
};

const writeRecent = (recent) => {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {
    // Storage blocked (private mode): recents just won't persist
  }
};

const htmlCache = new Map();
const symbolHtml = (latex) => {
  if (!htmlCache.has(latex)) htmlCache.set(latex, renderLatex(latex, false));
  return htmlCache.get(latex);
};

const SymbolButton = ({ latex, description, subject, onPick }) => {
  const html = symbolHtml(latex);
  return (
    <button
      type="button"
      className="symbol-button"
      title={`${description}${subject ? ` (${subject})` : ""}\n${latex}`}
      aria-label={description}
      // Keep focus (and the caret) in the field being edited
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onPick(latex, description)}
    >
      {html ? <span dangerouslySetInnerHTML={{ __html: html }} /> : <code>{latex}</code>}
    </button>
  );
};

/**
 * Maths / physics / chemistry symbol palette for the question editors.
 * Clicking a symbol calls onInsert with its LaTeX; the page inserts it at the caret.
 * @param {Function} props.onInsert - receives the LaTeX string to insert
 * @param {string} props.sectionName - used to open the matching subject tab
 */
const SymbolToolbar = ({ onInsert, sectionName = "" }) => {
  const [subjectKey, setSubjectKey] = useState("math");
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(readRecent);
  const [openCategories, setOpenCategories] = useState(() => new Set(SUBJECTS.map((s) => `${s.key}:0`)));
  const pickedByUser = useRef(false);

  // Follow the section's subject until the admin picks a tab themselves
  useEffect(() => {
    const detected = subjectForSection(sectionName);
    if (detected && !pickedByUser.current) setSubjectKey(detected);
  }, [sectionName]);

  const subject = SUBJECTS.find((s) => s.key === subjectKey);
  const results = useMemo(() => (query.trim() ? searchSymbols(query.trim()) : null), [query]);

  const pick = (latex, description) => {
    onInsert(latex);
    setRecent((prev) => {
      const next = [{ latex, description }, ...prev.filter((item) => item.latex !== latex)].slice(0, RECENT_LIMIT);
      writeRecent(next);
      return next;
    });
  };

  const selectSubject = (key) => {
    pickedByUser.current = true;
    setSubjectKey(key);
    setQuery("");
  };

  const toggleCategory = (key) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      // The toolbar can sit inside the question <form>; Enter inserts instead of submitting
      e.preventDefault();
      if (results && results.length) pick(results[0].latex, results[0].description);
    } else if (e.key === "Escape") {
      setQuery("");
    }
  };

  return (
    <div className="symbol-toolbar">
      <div className="symbol-toolbar-head">
        <div className="symbol-tabs">
          {SUBJECTS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`symbol-tab${!results && s.key === subjectKey ? " active" : ""}`}
              aria-pressed={!results && s.key === subjectKey}
              onClick={() => selectSubject(s.key)}
            >
              <i className={`bi ${s.icon}`}></i> {s.label}
            </button>
          ))}
        </div>
        <div className="symbol-search">
          <i className="bi bi-search"></i>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search all symbols — e.g. integral, sodium, Snell, ohm"
            aria-label="Search symbols"
          />
          {query && (
            <button type="button" className="symbol-search-clear" onClick={() => setQuery("")} aria-label="Clear search">
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      </div>

      {!results && recent.length > 0 && (
        <div className="symbol-recent">
          <span className="symbol-recent-label">Recent</span>
          <div className="symbol-grid">
            {recent.map((item) => (
              <SymbolButton key={item.latex} {...item} onPick={pick} />
            ))}
          </div>
        </div>
      )}

      <div className="symbol-panel">
        {results ? (
          results.length ? (
            <>
              <p className="symbol-results-count">
                {results.length === SEARCH_LIMIT ? `First ${SEARCH_LIMIT}` : results.length} match
                {results.length === 1 ? "" : "es"} across maths, physics and chemistry
              </p>
              <div className="symbol-grid">
                {results.map((item) => (
                  <SymbolButton key={`${item.latex}|${item.description}`} {...item} onPick={pick} />
                ))}
              </div>
            </>
          ) : (
            <p className="symbol-empty">No symbols match “{query.trim()}”.</p>
          )
        ) : (
          subject.categories.map((category, index) => {
            const key = `${subject.key}:${index}`;
            const open = openCategories.has(key);
            return (
              <div key={key} className="symbol-category">
                <button
                  type="button"
                  className="symbol-category-header"
                  aria-expanded={open}
                  onClick={() => toggleCategory(key)}
                >
                  <span>{category.title}</span>
                  <span className="symbol-category-meta">
                    <span className="symbol-category-count">{category.symbols.length}</span>
                    <i className={`bi bi-chevron-down symbol-category-toggle${open ? " expanded" : ""}`}></i>
                  </span>
                </button>
                {open && (
                  <div className="symbol-grid symbol-category-content">
                    {category.symbols.map(([latex, description]) => (
                      <SymbolButton key={`${latex}|${description}`} latex={latex} description={description} onPick={pick} />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <p className="symbol-hint">
        <i className="bi bi-info-circle"></i> Click in a question or option field, then click a symbol to insert its code at
        the cursor. Hover a symbol to see its LaTeX.
      </p>
    </div>
  );
};

export default SymbolToolbar;
